import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Download, Trash2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const STATUSES = [
  { value: "", label: "All" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "proposal", label: "Proposal in progress" },
  { value: "closed", label: "Closed" },
  { value: "spam", label: "Spam" },
];

const STATUS_STYLE = {
  new: "bg-copper/15 text-copper",
  contacted: "bg-jade/15 text-jade",
  proposal: "bg-jade/25 text-jade",
  closed: "bg-mist/60 text-ink/60",
  spam: "bg-ink/10 text-ink/55",
};

function fmt(dt) {
  if (!dt) return "—";
  try { return new Date(dt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return dt; }
}

export default function EnquiriesList() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    return api.get("/admin/enquiries", { params }).then((r) => setItems(r.data || []));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status]);

  const onSearch = (v) => { setQ(v); setTimeout(load, 0); };

  const updateOpen = async (patch) => {
    if (!open) return;
    setBusy(true);
    try {
      const r = await api.put(`/admin/enquiries/${open.enquiry_id}`, patch);
      setOpen(r.data);
      setItems((prev) => prev.map((x) => (x.enquiry_id === r.data.enquiry_id ? r.data : x)));
      toast.success("Saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!open) return;
    if (!window.confirm(`Delete enquiry from ${open.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/enquiries/${open.enquiry_id}`);
      setItems((prev) => prev.filter((x) => x.enquiry_id !== open.enquiry_id));
      setOpen(null);
      toast.success("Deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/enquiries/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `abbotasia-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch {
      toast.error("Could not export.");
    }
  };

  return (
    <div data-testid="admin-enquiries">
      <div className="flex items-start justify-between gap-6">
        <div>
          <p className="eyebrow">Inbox</p>
          <h1 className="mt-3 font-serif text-4xl text-jade font-light">Enquiries</h1>
          <p className="mt-3 text-ink/65 text-[14px] font-light max-w-xl">
            Every contact-form submission. Move them through the status pipeline and add internal notes. Enquiries never leave this admin area.
          </p>
        </div>
        <button onClick={exportCsv} data-testid="enquiries-export-csv" className="btn-secondary inline-flex items-center gap-2">
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 border-b border-mist pb-2 flex-1 min-w-[280px]">
          <Search size={14} className="text-ink/50" />
          <input
            data-testid="enquiries-search"
            type="text"
            placeholder="Search name, email, phone, notes…"
            value={q}
            onChange={(e) => onSearch(e.target.value)}
            className="flex-1 bg-transparent outline-none text-[14px]"
          />
        </div>
        <select data-testid="enquiries-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="border border-mist px-3 py-2 text-[13px] bg-ivory">
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      <div className="mt-8 grid lg:grid-cols-12 gap-8">
        {/* List */}
        <div className="lg:col-span-5 space-y-2">
          {items.map((e) => (
            <button
              key={e.enquiry_id}
              type="button"
              onClick={() => setOpen(e)}
              data-testid={`enquiry-${e.enquiry_id}`}
              className={`w-full text-left border ${open?.enquiry_id === e.enquiry_id ? "border-jade" : "border-mist hover:border-jade"} px-4 py-3 transition-colors`}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="font-serif text-[16px] text-jade truncate">{e.name}</p>
                <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 ${STATUS_STYLE[e.status] || "bg-mist/60 text-ink/60"}`}>{e.status || "new"}</span>
              </div>
              <p className="text-[12px] text-ink/55 mt-1 truncate">{e.email} · {fmt(e.created_at)}</p>
              <p className="text-[12px] text-ink/70 mt-1 truncate">{e.notes}</p>
            </button>
          ))}
          {items.length === 0 && <p className="text-ink/55 text-sm italic">No enquiries.</p>}
        </div>

        {/* Detail */}
        <div className="lg:col-span-7">
          {open ? (
            <div data-testid="enquiry-detail" className="border border-mist p-6 space-y-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="eyebrow text-copper">{open.enquiry_id}</p>
                  <p className="mt-2 font-serif text-2xl text-jade">{open.name}</p>
                  <p className="text-[13px] text-ink/65">{open.email} · {open.phone}</p>
                  <p className="text-[12px] text-ink/50 mt-1">Received {fmt(open.created_at)}</p>
                </div>
                <select
                  data-testid="enquiry-status-select"
                  value={open.status || "new"}
                  onChange={(e) => updateOpen({ status: e.target.value })}
                  disabled={busy}
                  className="border border-mist px-3 py-2 text-[13px] bg-ivory"
                >
                  {STATUSES.filter(s => s.value).map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4 text-[13px] text-ink/80">
                {open.destinations?.length > 0 && <p><span className="eyebrow text-ink/45 block">Destinations</span>{open.destinations.join(", ")}</p>}
                {open.other_destination && <p><span className="eyebrow text-ink/45 block">Other</span>{open.other_destination}</p>}
                {(open.date_start || open.travel_dates) && <p><span className="eyebrow text-ink/45 block">Travel dates</span>{open.date_start ? `${open.date_start} → ${open.date_end || "?"}` : open.travel_dates}</p>}
                {open.travellers && <p><span className="eyebrow text-ink/45 block">Travellers</span>{open.travellers}</p>}
                <p><span className="eyebrow text-ink/45 block">Party</span>{open.adults ?? 2} adults · {open.children ?? 0} children · {open.infants ?? 0} infants</p>
                {open.budget && <p><span className="eyebrow text-ink/45 block">Budget</span>{open.budget}</p>}
                <p><span className="eyebrow text-ink/45 block">Flights</span>{open.flights_required ? "Yes" : "No"}{open.home_airport ? ` · ${open.home_airport}` : ""}{open.cabin_class ? ` · ${open.cabin_class}` : ""}</p>
              </div>

              <div>
                <p className="eyebrow text-ink/45">Their notes</p>
                <p className="mt-2 text-[14px] text-ink/85 whitespace-pre-wrap leading-relaxed">{open.notes}</p>
              </div>

              <div>
                <label className="eyebrow text-ink/45">Internal notes</label>
                <textarea
                  data-testid="enquiry-internal-notes"
                  value={open.internal_notes || ""}
                  onChange={(e) => setOpen({ ...open, internal_notes: e.target.value })}
                  onBlur={(e) => updateOpen({ internal_notes: e.target.value })}
                  rows={4}
                  className="input-line mt-3 resize-y"
                  placeholder="Notes about this enquiry — never shown to the client."
                />
              </div>

              <div className="pt-2 flex items-center justify-between border-t border-mist mt-4">
                <button onClick={remove} data-testid="enquiry-delete" className="text-[12px] tracking-[0.14em] uppercase text-ink/55 hover:text-copper inline-flex items-center gap-2">
                  <Trash2 size={14} /> Delete enquiry
                </button>
                <a href={`mailto:${open.email}?subject=Your Abbot Asia enquiry`} className="text-[12px] tracking-[0.14em] uppercase text-jade hover:text-copper inline-flex items-center gap-2">
                  Reply by email <ArrowRight size={14} />
                </a>
              </div>
            </div>
          ) : (
            <div className="border border-mist p-6 text-ink/55 text-[14px] italic">Select an enquiry on the left.</div>
          )}
        </div>
      </div>
    </div>
  );
}
